import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  BookOpenCheck,
  CircleDollarSign,
  Landmark,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = {
  code: "",
  name: "",
  account_type: "asset",
  normal_balance: "debit",
  parent_code: "",
  description: "",
  active: true,
};

const TYPE_FALLBACK = {
  asset: "Aset",
  liability: "Kewajiban",
  equity: "Ekuitas",
  revenue: "Pendapatan",
  cogs: "Harga Pokok Penjualan",
  expense: "Beban",
};

const TYPE_TONE = {
  asset: "border-blue-200 bg-blue-50 text-blue-700",
  liability: "border-amber-200 bg-amber-50 text-amber-700",
  equity: "border-violet-200 bg-violet-50 text-violet-700",
  revenue: "border-emerald-200 bg-emerald-50 text-emerald-700",
  cogs: "border-orange-200 bg-orange-50 text-orange-700",
  expense: "border-rose-200 bg-rose-50 text-rose-700",
};

export default function Coa() {
  const { hasPerm } = useAuth();
  const canManage = hasPerm("coa.manage");
  const [accounts, setAccounts] = useState([]);
  const [types, setTypes] = useState([]);
  const [rules, setRules] = useState([]);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const typeMap = useMemo(() => Object.fromEntries(
    (types || []).map((item) => [item.key, item]),
  ), [types]);

  const load = async () => {
    setLoading(true);
    try {
      const [coaResponse, ruleResponse] = await Promise.all([
        api.get("/coa"),
        api.get("/coa/posting-rules"),
      ]);
      setAccounts(coaResponse.data?.accounts || []);
      setTypes(coaResponse.data?.types || []);
      setRules(ruleResponse.data?.rules || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "COA gagal dimuat");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return accounts.filter((account) => {
      if (typeFilter && account.account_type !== typeFilter) return false;
      if (statusFilter === "active" && account.active === false) return false;
      if (statusFilter === "inactive" && account.active !== false) return false;
      if (!needle) return true;
      return [account.code, account.name, account.description, account.system_key]
        .some((value) => String(value || "").toLowerCase().includes(needle));
    });
  }, [accounts, query, typeFilter, statusFilter]);

  const counts = useMemo(() => ({
    total: accounts.length,
    active: accounts.filter((account) => account.active !== false).length,
    system: accounts.filter((account) => account.is_system).length,
    custom: accounts.filter((account) => !account.is_system).length,
  }), [accounts]);

  const setAccountType = (accountType) => {
    const normal = typeMap[accountType]?.default_normal_balance
      || (["asset", "cogs", "expense"].includes(accountType) ? "debit" : "credit");
    setForm((current) => ({ ...current, account_type: accountType, normal_balance: normal }));
  };

  const openCreate = () => {
    const defaultNormal = typeMap.asset?.default_normal_balance || "debit";
    setForm({ ...EMPTY, normal_balance: defaultNormal });
    setEditId(null);
    setOpen(true);
  };

  const openEdit = (account) => {
    setForm({
      code: account.code || "",
      name: account.name || "",
      account_type: account.account_type || "asset",
      normal_balance: account.normal_balance || "debit",
      parent_code: account.parent_code || "",
      description: account.description || "",
      active: account.active !== false,
      is_system: account.is_system === true,
    });
    setEditId(account.id);
    setOpen(true);
  };

  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      toast.error("Kode dan nama akun wajib diisi");
      return;
    }
    setSaving(true);
    try {
      const body = {
        code: form.code.trim(),
        name: form.name.trim(),
        parent_code: form.parent_code.trim() || null,
        description: form.description.trim(),
      };
      if (!form.is_system) {
        body.account_type = form.account_type;
        body.normal_balance = form.normal_balance;
        body.active = form.active !== false;
      }
      if (editId) {
        await api.patch(`/coa/${editId}`, body);
        toast.success("Akun COA diperbarui");
      } else {
        await api.post("/coa", body);
        toast.success("Akun COA ditambahkan");
      }
      setOpen(false);
      setEditId(null);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Akun COA gagal disimpan");
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (account) => {
    if (!window.confirm(`Nonaktifkan akun ${account.code} - ${account.name}?`)) return;
    try {
      await api.delete(`/coa/${account.id}`);
      toast.success("Akun dinonaktifkan");
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Akun gagal dinonaktifkan");
    }
  };

  const restoreDefaults = async () => {
    try {
      const response = await api.post("/coa/restore-defaults");
      toast.success(response.data?.message || "Akun sistem dipulihkan");
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Akun sistem gagal dipulihkan");
    }
  };

  return (
    <div className="p-6 lg:p-10">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="overline text-zinc-500">KEUANGAN & AKUNTANSI</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Chart of Accounts</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-zinc-500">
            Master akun untuk jurnal debit/kredit KRAYA. Akun bertanda Sistem dipakai otomatis untuk memetakan penjualan, QRIS, HPP, persediaan, PPN, dan pembelian.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={load} disabled={loading} className="inline-flex items-center gap-2 border border-border bg-white px-4 py-2 text-sm font-semibold hover:bg-zinc-50 disabled:opacity-50">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          {canManage && (
            <>
              <button type="button" onClick={restoreDefaults} className="inline-flex items-center gap-2 border border-border bg-white px-4 py-2 text-sm font-semibold hover:bg-zinc-50">
                <ShieldCheck className="h-4 w-4" /> Pulihkan Akun Sistem
              </button>
              <button type="button" onClick={openCreate} className="inline-flex items-center gap-2 bg-[#002FA7] px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800">
                <Plus className="h-4 w-4" /> Akun Baru
              </button>
            </>
          )}
        </div>
      </div>

      <div className="mb-6 grid grid-cols-2 lg:grid-cols-4 grid-borders">
        {[
          { label: "Total Akun", value: counts.total, icon: Landmark },
          { label: "Akun Aktif", value: counts.active, icon: BookOpenCheck },
          { label: "Akun Sistem", value: counts.system, icon: ShieldCheck },
          { label: "Akun Custom", value: counts.custom, icon: CircleDollarSign },
        ].map((item) => (
          <div key={item.label} className="bg-white p-5">
            <item.icon className="h-5 w-5 text-blue-600" />
            <div className="mt-3 overline text-zinc-500">{item.label}</div>
            <div className="mt-1 font-display text-3xl font-black tabular-nums">{item.value}</div>
          </div>
        ))}
      </div>

      <div className="mb-6 border border-border bg-white p-5">
        <div className="mb-4 flex items-center gap-2">
          <BookOpenCheck className="h-5 w-5 text-blue-600" />
          <div>
            <div className="font-semibold">Pemetaan jurnal otomatis</div>
            <div className="text-xs text-zinc-500">Aturan berikut menggunakan akun sistem dan tidak bergantung pada nama akun yang tampil.</div>
          </div>
        </div>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {rules.map((rule) => (
            <div key={rule.key} className="border border-border bg-zinc-50 p-4">
              <div className="text-sm font-bold">{rule.label}</div>
              <div className="mt-3 space-y-2">
                {(rule.lines || []).map((line, index) => (
                  <div key={`${rule.key}-${index}`} className="flex items-start justify-between gap-3 text-xs">
                    <div>
                      <span className={`mr-2 inline-flex min-w-12 justify-center border px-1.5 py-0.5 font-bold uppercase ${line.side === "debit" ? "border-blue-200 bg-blue-50 text-blue-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>{line.side}</span>
                      <span className="font-mono">{line.account?.code || "-"}</span>
                    </div>
                    <div className="text-right"><div className="font-semibold">{line.account?.name || "-"}</div><div className="text-zinc-500">{line.basis}</div></div>
                  </div>
                ))}
              </div>
              {rule.note && <div className="mt-3 border-t border-amber-200 pt-2 text-[11px] leading-5 text-amber-700">{rule.note}</div>}
            </div>
          ))}
        </div>
      </div>

      <div className="mb-4 flex flex-col gap-3 lg:flex-row">
        <label className="relative flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-400" />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Cari kode, nama akun, atau mapping..." className="w-full border border-border py-2 pl-9 pr-3 text-sm" />
        </label>
        <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)} className="border border-border px-3 py-2 text-sm">
          <option value="">Semua Tipe</option>
          {(types.length ? types : Object.entries(TYPE_FALLBACK).map(([key, label]) => ({ key, label }))).map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
        </select>
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="border border-border px-3 py-2 text-sm">
          <option value="active">Aktif</option>
          <option value="inactive">Nonaktif</option>
          <option value="all">Semua Status</option>
        </select>
      </div>

      <div className="overflow-hidden border border-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] text-sm">
            <thead className="border-b border-border bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-4 py-3">Kode</th><th className="px-4 py-3">Nama Akun</th><th className="px-4 py-3">Tipe</th><th className="px-4 py-3">Saldo Normal</th><th className="px-4 py-3">Parent</th><th className="px-4 py-3">Mapping</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filtered.map((account) => (
                <tr key={account.id} className="hover:bg-zinc-50/70">
                  <td className="px-4 py-3 font-mono font-bold">{account.code}</td>
                  <td className="px-4 py-3"><div className="font-semibold">{account.name}</div>{account.description && <div className="mt-1 max-w-md text-xs text-zinc-500">{account.description}</div>}</td>
                  <td className="px-4 py-3"><span className={`inline-flex border px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${TYPE_TONE[account.account_type] || "border-zinc-200 bg-zinc-50"}`}>{typeMap[account.account_type]?.label || TYPE_FALLBACK[account.account_type] || account.account_type}</span></td>
                  <td className="px-4 py-3 font-semibold uppercase">{account.normal_balance}</td>
                  <td className="px-4 py-3 font-mono text-xs">{account.parent_code || "-"}</td>
                  <td className="px-4 py-3">{account.is_system ? <span className="inline-flex items-center gap-1 border border-blue-200 bg-blue-50 px-2 py-1 text-[10px] font-bold uppercase text-blue-700"><ShieldCheck className="h-3 w-3" /> {account.system_key}</span> : <span className="text-xs text-zinc-400">Custom</span>}</td>
                  <td className="px-4 py-3"><span className={`inline-flex px-2 py-1 text-[10px] font-bold uppercase ${account.active !== false ? "bg-emerald-100 text-emerald-700" : "bg-zinc-200 text-zinc-600"}`}>{account.active !== false ? "Aktif" : "Nonaktif"}</span></td>
                  <td className="px-4 py-3 text-right">
                    {canManage && <button type="button" onClick={() => openEdit(account)} className="p-2 hover:bg-zinc-100" title="Edit akun"><Pencil className="h-4 w-4" /></button>}
                    {canManage && !account.is_system && account.active !== false && <button type="button" onClick={() => deactivate(account)} className="p-2 text-rose-600 hover:bg-rose-50" title="Nonaktifkan akun"><Trash2 className="h-4 w-4" /></button>}
                  </td>
                </tr>
              ))}
              {!filtered.length && <tr><td colSpan="8" className="px-4 py-10 text-center text-zinc-500">Tidak ada akun sesuai filter.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl rounded-none">
          <DialogHeader><DialogTitle>{editId ? "Edit" : "Tambah"} Akun COA</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="overline">Kode Akun</label><input value={form.code} onChange={(event) => setForm({ ...form, code: event.target.value.toUpperCase() })} className="mt-1 w-full border border-border px-3 py-2 font-mono" placeholder="1101" /></div>
            <div><label className="overline">Nama Akun</label><input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} className="mt-1 w-full border border-border px-3 py-2" placeholder="Kas" /></div>
            <div><label className="overline">Tipe Akun</label><select disabled={form.is_system} value={form.account_type} onChange={(event) => setAccountType(event.target.value)} className="mt-1 w-full border border-border px-3 py-2 disabled:bg-zinc-100">{(types.length ? types : Object.entries(TYPE_FALLBACK).map(([key, label]) => ({ key, label }))).map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}</select></div>
            <div><label className="overline">Saldo Normal</label><select disabled={form.is_system} value={form.normal_balance} onChange={(event) => setForm({ ...form, normal_balance: event.target.value })} className="mt-1 w-full border border-border px-3 py-2 disabled:bg-zinc-100"><option value="debit">Debit</option><option value="credit">Kredit</option></select></div>
            <div className="col-span-2"><label className="overline">Parent Account (opsional)</label><select value={form.parent_code} onChange={(event) => setForm({ ...form, parent_code: event.target.value })} className="mt-1 w-full border border-border px-3 py-2"><option value="">Tanpa Parent</option>{accounts.filter((account) => account.active !== false && account.id !== editId).map((account) => <option key={account.id} value={account.code}>{account.code} - {account.name}</option>)}</select></div>
            <div className="col-span-2"><label className="overline">Deskripsi</label><textarea rows="3" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} className="mt-1 w-full border border-border px-3 py-2" /></div>
            {!form.is_system && <label className="col-span-2 flex items-center justify-between border border-border p-3"><div><div className="font-semibold">Akun aktif</div><div className="text-xs text-zinc-500">Akun nonaktif tetap tersimpan tetapi tidak digunakan sebagai referensi baru.</div></div><input type="checkbox" checked={form.active !== false} onChange={(event) => setForm({ ...form, active: event.target.checked })} /></label>}
            {form.is_system && <div className="col-span-2 border border-blue-200 bg-blue-50 p-3 text-xs leading-5 text-blue-800">Akun sistem boleh diganti kode, nama, parent, dan deskripsinya. Tipe akun, saldo normal, mapping internal, serta status aktif dikunci agar jurnal otomatis tetap konsisten.</div>}
          </div>
          <div className="mt-4 flex gap-2"><button type="button" onClick={() => setOpen(false)} className="flex-1 border border-border py-3">Batal</button><button type="button" onClick={save} disabled={saving} className="flex-1 bg-[#002FA7] py-3 font-semibold text-white disabled:opacity-50">{saving ? "Menyimpan..." : "Simpan"}</button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
